<template>
  <div>
    <div v-if="data.validUntil !== null"
         class="asd-warning"
    >
      <i class="fas fa-trash"></i> Tento úkol byl vyhozen do koše
    </div>
    <div class="asd-form-section heading-section">
      <h1>
        <div class="context-menu"
             @click="back"
        >
          <i class="fas fa-arrow-left context-menu-icon"></i>
        </div>
        <ContextMenu
          :items="[
            { icon: 'fa-trash', label: 'Smazat úkol', action: disableTask },
            { icon: 'fa-dolly', label: 'Přesunout úkol', action: showMoveDialog },
            {
              icon: 'fa-people-carry',
              label: 'Vytvořit požadavek ve flexiservisu',
              action: openFlexiservisExportModal,
            },
          ]"
        >
          <template #icon>
            <i class="fas fa-bars"></i>
          </template>
        </ContextMenu>
        {{ taskLabel }}
      </h1>
      <div class="asd-badge">
        {{ taskType.name }}
      </div>
    </div>
    <div class="asd-form-section">
      <div class="section-title">
        <h5>
          <i class="fas fa-th-large"></i>
          Vlastnosti
        </h5>
        <div
          v-if="data.validUntil === null && $store.getters.currentUserHasAction('EDIT_TASK')"
          class="section-title-controls"
        >
          <ActiveButton v-if="editProperties"
                        class="asd-btn-confirm"
                        :click="save"
          >
            <i class="fas fa-save"></i>
          </ActiveButton>
          <ActiveButton
            v-if="editProperties"
            v-help.mouse="`Neukládat a zrušit editaci`"
            class="asd-btn-cancel"
            :click="cancel"
          >
            <i class="fas fa-times"></i>
          </ActiveButton>
          <button v-else
                  class="asd-btn-edit"
                  @click="editProperties = true"
          >
            <i class="fas fa-pencil-alt"></i>
          </button>
        </div>
      </div>
      <PropertyForm
        v-if="editProperties"
        v-model="data.propertyValues"
        class="asd-form"
        :propertyKeys="taskPropertyKeys"
        :meta="{ entityType: taskType, task: task }"
      ></PropertyForm>
      <PropertyTable
        v-else
        v-model="data.propertyValues"
        class="asd-form"
        :propertyKeys="taskPropertyKeys"
      ></PropertyTable>
      <StateHistory v-for="h in statesHistory"
                    :key="h.key"
                    :value="h"
      ></StateHistory>
    </div>

    <div v-if="$store.getters.modules.comments"
         class="asd-form-section"
    >
      <div class="section-title">
        <h5>
          <i class="fas fa-comments"></i>
          Diskuse
        </h5>
      </div>
      <Comments class="asd-form"></Comments>
    </div>

    <div v-if="$store.getters.modules.attachments"
         class="asd-form-section"
    >
      <div class="section-title">
        <h5>
          <i class="fas fa-paperclip"></i>
          Přílohy
        </h5>
      </div>
      <ProjectAttachments class="asd-form"></ProjectAttachments>
    </div>
    <Modal v-if="showMoveEditor">
      <template #default>
        Vyberte projekt pod který chcete tento úkol přesunout.
        <Multiselect
          v-model="projectToMoveTo"
          label="label"
          trackBy="id"
          valueByPath="id"
          :options="projectsForMove"
          :returnAsSingle="true"
          :max="1"
        >
        </Multiselect>
      </template>
      <template #controls>
        <button class="asd-btn-cancel"
                @click="showMoveEditor = false"
        >
          Zrušit
        </button>
        <ActiveButton class="asd-btn-confirm"
                      :click="moveTask"
        >
          Přesunout
        </ActiveButton>
      </template>
    </Modal>
    <Modal v-if="showFlexiservisExport">
      <template #default>
        <table style="min-width: 40vw">
          <tr>
            <td>Pracovnik pro export</td>
            <td>
              <Multiselect
                v-model="flexiservisExportPracovnik"
                label="label"
                trackBy="id"
                valueByPath="id"
                :options="availableFlexibeeResources"
                :max="1"
                :returnAsSingle="true"
              ></Multiselect>
            </td>
          </tr>
        </table>
      </template>
      <template #controls>
        <button class="asd-btn-cancel"
                @click="showFlexiservisExport = false"
        >
          Zavřít
        </button>
        <ActiveButton class="asd-btn-confirm"
                      :click="createFlexiservisExportRequest"
        >
          Exportovat do flexi
        </ActiveButton>
      </template>
    </Modal>
  </div>
</template>

<script>
import _ from 'lodash'
import Axios from 'axios'
import labels from 'CORE/utils/labels'
import PropertyTable from 'CORE/components/data-type/property-detail-table'
import PropertyForm from 'CORE/components/form/property-form'
import TaskDataMixin from 'CORE/mixins/task-data'
import ProjectAttachments from 'CORE/components/project/attachments'
import Comments from 'CORE/components/comments/comments'
import uuidValidate from 'uuid-validate'
import ActiveButton from 'CORE/components/form/active-button'
import StateHistory from 'CORE/components/entity-states/history'
import ContextMenu from 'CORE/components/context-menu'
import EventBus from 'CORE/bus'
import Modal from 'CORE/components/modal/modal'
import Multiselect from 'CORE/components/utilities/vue-multiselect-ext'

export default {
    name: 'TaskDetail',
    components: {
        PropertyForm,
        PropertyTable,
        ProjectAttachments,
        Comments,
        ActiveButton,
        StateHistory,
        ContextMenu,
        Modal,
        Multiselect,
    },
    mixins: [TaskDataMixin],
    props: {
        taskId: {
            required: true,
            type: String,
            validator: value => uuidValidate(value, 4),
        },
    },
    data() {
        return {
            data: {},
            editProperties: true,
            showMoveEditor: false,
            projectToMoveTo: null,
            showFlexiservisExport: false,
            flexiservisExportPracovnik: null,
        }
    },
    computed: {
        taskLabel() {
            return labels.task(this.data)
        },
        taskPropertyKeys() {
            return _.map(_.get(this.taskType, 'propertyType', {}), pt => pt.key)
        },
        statesHistory() {
            const entityStateProperties = _.filter(
                _.get(this.taskType, 'propertyType', {}),
                pt => pt.type === 'ENTITY_STATE'
            )
            return _.map(entityStateProperties, p => {
                const history = _.get(this.data.propertyValues, `${p.key}.value.history`, [])
                return {
                    key: p.key,
                    name: p.name,
                    history,
                }
            })
        },
        projectsForMove() {
            return _.map(this.$store.getters.projects, p => {
                return {
                    id: p.id,
                    label: labels.project(this.$store.getters.getProjectById(p.id)),
                }
            })
        },
        availableFlexibeeResources() {
            const ret = _.chain(_.get(this.data.propertyValues, `system-team.value.value`, []))
                .map(resourceId => {
                    const resource = this.$store.getters.resourceById(resourceId)
                    return { id: resourceId, label: labels.resource(resource) }
                })
                .value()

            return ret
        },
    },
    created() {
        this.data = this.$store.getters.taskById(this.taskId)
        console.assert(uuidValidate(_.get(this.data, 'id')), 'Invalid Task')
    },
    methods: {
        async disableTask() {
            try {
                await this.$store.dispatch('disableTask', this.data.id)
                await this.$store.dispatch('tasksLoad')
                EventBus.$emit('FLASH_OK', { message: 'Uloženo' })
            } catch (e) {
                EventBus.$emit('FLASH_ERROR', e)
            }
        },
        async save() {
            try {
                await this.$store.dispatch('updateTask', this.data)
                await this.$store.dispatch('tasksLoad')
                EventBus.$emit('FLASH_OK', { message: 'Uloženo' })
                this.$emit('done')
                this.editProperties = false
            } catch (e) {
                EventBus.$emit('FLASH_ERROR', e)
            }
        },
        async cancel() {
            await this.$store.dispatch('tasksLoad')
            this.$emit('done')
        },
        showMoveDialog() {
            if (this.$store.getters.currentUserHasAction('EDIT_TASK')) {
                this.showMoveEditor = true
            } else {
                EventBus.$emit('FLASH_USER_ERROR', {
                    message: 'Na přesunutí projektu nemáte oprávnění (editovat úkol).',
                })
            }
        },
        async moveTask() {
            console.info(`moving task to ${this.projectToMoveTo}`)
            this.data.project.id = this.projectToMoveTo
            try {
                await this.$store.dispatch('updateTask', this.data)
                await this.$store.dispatch('tasksLoad')
                EventBus.$emit('FLASH_OK', { message: 'Přesunuto' })
            } catch (e) {
                EventBus.$emit('FLASH_ERROR', e)
            } finally {
                this.showMoveEditor = false
            }
        },
        async openFlexiservisExportModal() {
            this.showFlexiservisExport = true
        },
        async createFlexiservisExportRequest() {
            try {
                const params = {
                    overrides: {
                        pracovnikResource: this.flexiservisExportPracovnik,
                    },
                }
                const response = await Axios.get(`/workflow/flexiservis/create_pozadavek/${this.data.id}`, {
                    params,
                })
                console.info(response.data)
                EventBus.$emit('FLASH_OK', { message: 'Vytvoreno' })
            } catch (e) {
                EventBus.$emit('FLASH_ERROR', e)
            } finally {
                this.showFlexiservisExport = false
            }
        },
        back() {
            this.$emit('back')
        },
    },
}
</script>

<style lang="scss" scoped>
.asd-warning {
    color: black;
    background: repeating-linear-gradient(
        135deg,
        rgba(231, 55, 39, 0) 0px,
        rgba(231, 55, 39, 0) 9px,
        rgba(231, 55, 39, 0.7) 10px,
        rgba(231, 55, 39, 0.7) 19px
    );
    font-size: 2rem;
    font-weight: bold;
    padding: 30px;
    text-align: center;
}
</style>
